'use client';

import { authSystemForTreatments, type AuthSystem } from '@/app/utils/auth-system';
import type { TreatmentsMap } from '@/app/utils/experimentation';
import { createContext, useContext } from 'react';

/**
 * TreatmentsContext provides a map of all experiment treatments assigned to the
 * current user.
 *
 * It is exposed to client components and accessible via `useTreatments`.
 *
 * For server components, treatments can be read directly from the cookies using experimentation
 * utils getAllTreatments and getTreatment.
 */
export const TreatmentsContext = createContext<TreatmentsMap>({});

export const useTreatments = () => useContext(TreatmentsContext);

/**
 * Returns the one auth system selected for this browser's experiment bucket.
 * Authentication consumers should use this instead of reading an experiment
 * directly so they cannot diverge across modals and embedded checkout.
 */
export const useAuthSystem = (): AuthSystem => authSystemForTreatments(useTreatments());

export default function TreatmentsProvider({
  treatments,
  children,
}: {
  treatments: TreatmentsMap;
  children: React.ReactNode;
}) {
  return <TreatmentsContext.Provider value={treatments}>{children}</TreatmentsContext.Provider>;
}
